import React, { Fragment, useState, useEffect, useRef } from 'react';
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';
import { connect } from 'react-redux';
import { Row, Col, Spin, Tag } from 'antd';
import { config } from '../../constant';
import ReactEcharts from '../../Echarts/Echarts';
import { getChartDataWithoutSelection, getChartDataWithSelection } from '../../ChartCalls';
import StyledTable from '../../../../../commons/styles/table';
import { barTableMockJson } from '../../../Mock/MockJson';
import { ErrorComponent, NoRecordComponent } from '../../Utils';


import '../../CSS/Table.css';

const BarTable = (props) => {
  const [option, setOption] = useState(getInitialOption(props));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [refreshDateTime, setRefreshDateTime] = useState(moment());
  const [errorInAPI, setErrorInAPI] = useState(false);
  const [noDataInDB, setNoDataInDB] = useState(false);
  let timeTicket = useRef(null);
    const requestCheck = useRef(true);
    const abortController = useRef(new window.AbortController());
  
    const {
      graphprops,
      selectionDate,
      selectionMonth,
      DeviceCode,
      selectionDateRange,
      dayMonthYear,
      currentTimeZone,
    } = props;
  
    useEffect(() => {
      if (timeTicket.current) {
        clearInterval(timeTicket.current);
      }
      let refreshTime = graphprops?.refreshTime ? graphprops.refreshTime * 60000 : config.defaultRefreshTime;
      timeTicket.current = setInterval(() => {
        if (requestCheck.current) {
          getData();
        }
      }, refreshTime);
      return () => {
        abortController.current.abort();
        clearInterval(timeTicket.current);
      };
    }, []);
  
    useEffect(() => {
      getData();
    }, [
      selectionDate,
      selectionMonth,
      selectionDateRange,
      DeviceCode,
      dayMonthYear,
      props.translationCache
    ]);

  const getData = async () => {
    setLoading(true);
    const optionClone = cloneDeep(option);
    let json = [];
    const { graphprops } = props;
   if (graphprops && graphprops.enableMock && graphprops.enableMock === true) {
      json = barTableMockJson;
      requestCheck.current = true;
    } else {
      if (graphprops?.graph === 'rangeBar' && graphprops?.currentDateData !== undefined) {
        json = graphprops.currentDateData;
      } else {
        if (
          graphprops &&
          ((graphprops.DashboardLevelSelection === 'datePicker' ||
            graphprops.GraphLevelSelection === 'datePicker') &&
            selectionDate) || 
          (
            (graphprops.DashboardLevelSelection === 'monthPicker' ||
              graphprops.GraphLevelSelection === 'monthPicker') &&
            selectionMonth) || 
          (
            (graphprops.DashboardLevelSelection === 'dateRangePicker' ||
              graphprops.GraphLevelSelection === 'dateRangePicker') &&
            selectionDateRange) || 
          (
            (graphprops.DashboardLevelSelection === 'dateMonthYearPicker' ||
              graphprops.GraphLevelSelection === 'dateMonthYearPicker') &&
            dayMonthYear) ||
          (
            (graphprops.DashboardLevelSelection === 'deviceSelection' ||
              graphprops.GraphLevelSelection === 'deviceSelection') &&
            DeviceCode)
        ) {
          json = await getChartDataWithSelection({
            graphprops,
            selectionDate,
            selectionMonth,
            selectionDateRange,
            DeviceCode,
            dayMonthYear,
            abortController: abortController.current,
            currentTimeZone,
          });
          requestCheck.current = true;
        } else if (
          graphprops.GraphLevelSelection === 'none' &&
          graphprops.DashboardLevelSelection   === 'none'
        ) {
          json = await getChartDataWithoutSelection({
            graphprops,
            abortController: abortController.current,
            DeviceCode,
            TimeZone: currentTimeZone,
          });
          requestCheck.current = true;
        }
      }
    }

    handleDataResponse(json, graphprops, optionClone);
    setLoading(false);
  };


  const handleDataResponse = (json, graphprops, optionClone) => {
    let errorInAPI = false;
    let noDataInDB = false;

    if (json && json.message) {
      errorInAPI = true;
    } else if (Array.isArray(json) && json.length === 0 && graphprops.enableMock !== true) {
      noDataInDB = true;
    } else {
      const columns = graphprops[graphprops.graph]?.columns ? JSON.parse(graphprops[graphprops.graph].columns) : [];
      const { series, legends, xaxisLabels } = processData(json, graphprops);
      optionClone.series = series;
      optionClone.legend.data = legends;
      optionClone.xAxis[0].data = xaxisLabels;

      setOption(optionClone);
      setData(json);
      setColumns(columns);
      setRefreshDateTime(moment());
    }
    setErrorInAPI(errorInAPI);
    setNoDataInDB(noDataInDB);
  };

  const processData = (json, graphprops) => {
    let tagnames = [];
    let series = [];
    let legends = [];
    let xaxisLabels = [];
    const label = {
      show: true,
      formatter: '{c}',
      fontSize: 16
    };

    json.map((val) => {
      if (!tagnames.includes(val.Name)) {
        tagnames.push(val.Name);
        legends.push({
          name: val.Description,
          icon: 'roundRect',
          textStyle: {
            fontSize: 18,
            fontWeight: 'bold'
          }
        });
      }
      const valueDate = moment(val.DateTime).utc().format('DD/MM/YYYY HH:mm:ss');
      if (!xaxisLabels.includes(valueDate)) {
        xaxisLabels.push(valueDate);
      }
      return null;
    });

    tagnames.map((tagval, tagindex) => {
      let data = [];
      json.map((jsonval) => {
        if (tagval === jsonval.Name) {
          data.push({
            value: jsonval.Value,
            itemStyle: {
              color: graphprops && graphprops.colors && graphprops.colors[tagindex] || 'steelblue'
            }
          });
        }
        return null;
      });
      series.push({
        name: legends[tagindex].name,
        data: data,
        type: 'bar',
        label,
        axisLabel: { fontSize: 15 }
      });
      return null;
    });

    return { series, legends, xaxisLabels };
  };

  const onChartClick = (cellData) => {
    const newObject = data.filter((predict) => {
      const dateTime = typeof predict.DateTime === 'string' ? predict.DateTime : moment(predict.DateTime).utc().format('DD/MM/YYYY HH:mm:ss');
      return dateTime === cellData.name;
    });
    if (newObject.length > 0) {
      setDataSource(newObject[cellData.seriesIndex].Action);
      // setSelectedDate(cellData.name);
    }
  };

  return (
    <Fragment>
      {props.graphprops && props.graphprops.checked ? (
        <Tag className="graphTag" color="blue">
          {refreshDateTime ? moment(refreshDateTime).format('YYYY-MM-DD HH:mm:ss') : ''}
        </Tag>
      ) : null}
      <span>
        {!loading ? (
          <div style={{ padding: '10px' }}>
            <Row>
              <Col span={24}>
                {noDataInDB || errorInAPI ? (
                  noDataInDB ? (
                    <NoRecordComponent />
                  ) : (
                    <ErrorComponent />
                  )
                ) : (
                  <ReactEcharts
                    option={option}
                    notMerge={true}
                    onEvents={{ click: onChartClick }}
                    lazyUpdate={true}
                    className="react_for_echarts"
                  />
                )}
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <div style={{ padding: '20px' }}>
                  <StyledTable size="small"
                    className="barTable"
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                    
                    style={{ padding: '0px 10px', marginTop: '12px' }}
                    scroll={{ y: 240 }}
                    bordered
                  />
                </div>
              </Col>
            </Row>
          </div>
        ) : (
        <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              <Spin spinning={loading} tip="Loading..." />
            </div>
        )}
      </span>
    </Fragment>
  );
};

const getInitialOption = (props) => ({
  color: ['tomato', 'steelblue', 'orange', 'yellow', 'green'],
  legend: {
    textStyle: { color: props.theme.algorithm === "dark" ? 'white' : 'black' },
    data: [{ name: 'test1', icon: 'roundRect', textStyle: { fontSize: 15 } }]
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' }
  },
  toolbox: {
    showTitle: false,
    itemSize: 25,
    feature: {
      saveAsImage: {
        backgroundColor: 'black',
        title: 'download',
        name: 'Chart',
        iconStyle: { borderColor: '#8c8c8c', borderWidth: 2 }
      }
    }
  },
  xAxis: [{
    type: 'category',
    data: [],
    axisLine: { lineStyle: { color: props.theme.algorithm === "dark" ? 'white' : 'black' } },
    axisTick: { show: false },
    axisLabel: { color: props.theme.algorithm === "dark" ? 'white' : 'black' }
  }],
  yAxis: [{
    type: 'value',
    axisLine: { lineStyle: { color: props.theme.algorithm === "dark" ? 'white' : 'black' } },
    axisTick: { lineStyle: { color: props.theme.algorithm === "dark" ? 'white' : 'black' } },
    axisLabel: { color: props.theme.algorithm === "dark" ? 'white' : 'black' }
  }],
  series: []
});


const mapStateToProps = (state) => {
  return {
    theme: state.ThemeReducer,
    translationCache: state.TranslationReducer.translationCache,
    currentTimeZone: state.LayoutReducer.currentTimeZone
  };
};


export default connect(mapStateToProps)(BarTable);